import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useTheme } from '../../context/ThemeContext';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  Ticket,
  Users,
  BarChart3,
  Settings,
  Bell,
  LogOut,
  Sun,
  Moon,
  Tag,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '../ui/button';

const navItems = [
  { to: '/backoffice', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { to: '/backoffice/tickets', icon: Ticket, label: 'Tickets' },
  { to: '/backoffice/analytics', icon: BarChart3, label: 'Analytiques' },
  { to: '/backoffice/users', icon: Users, label: 'Utilisateurs', adminOnly: true },
  { to: '/backoffice/tags', icon: Tag, label: 'Tags' },
  { to: '/backoffice/notifications', icon: Bell, label: 'Notifications' },
];

export const Sidebar = ({ collapsed, onToggle }) => {
  const { user, logout, isAdmin } = useAuth();
  const { unreadCount } = useNotifications();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <aside
      data-testid="backoffice-sidebar"
      className={cn(
        'h-screen bg-zinc-900 text-zinc-300 border-r border-zinc-800 flex flex-col transition-all duration-300',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-zinc-800">
        {!collapsed && (
          <img 
            src="/logo-freelanceurs.png" 
            alt="Freelanceurs" 
            className="h-8 w-auto"
          />
        )}
        {collapsed && (
          <img 
            src="/logo-freelanceurs.png" 
            alt="Freelanceurs" 
            className="h-6 w-auto mx-auto"
          />
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="text-zinc-400 hover:text-white hover:bg-zinc-800"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <ul className="space-y-1 px-2">
          {navItems.map(item => {
            if (item.adminOnly && !isAdmin) return null;
            
            return (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.exact}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                      isActive
                        ? 'bg-emerald-600/20 text-emerald-400'
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                    )
                  }
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {!collapsed && (
                    <span className="truncate">{item.label}</span>
                  )}
                  {item.label === 'Notifications' && unreadCount > 0 && (
                    <span className={cn(
                      'bg-red-500 text-white text-xs rounded-full',
                      collapsed ? 'absolute top-0 right-0 w-4 h-4 flex items-center justify-center' : 'px-2 py-0.5 ml-auto'
                    )}>
                      {unreadCount}
                    </span>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className="border-t border-zinc-800 p-4 space-y-3">
        {/* Theme toggle */}
        <Button
          variant="ghost"
          size={collapsed ? 'icon' : 'default'}
          onClick={toggleTheme}
          className={cn(
            'text-zinc-400 hover:text-white hover:bg-zinc-800',
            !collapsed && 'w-full justify-start'
          )}
        >
          {theme === 'dark' ? (
            <Sun className="w-5 h-5" />
          ) : (
            <Moon className="w-5 h-5" />
          )}
          {!collapsed && <span className="ml-3">Mode {theme === 'dark' ? 'clair' : 'sombre'}</span>}
        </Button>

        {/* User info */}
        {!collapsed && user && (
          <div className="flex items-center gap-3 px-3 py-2 bg-zinc-800 rounded-lg">
            <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white font-medium text-sm">
              {user.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user.name}</p>
              <p className="text-xs text-zinc-500 capitalize">{user.role}</p>
            </div>
          </div>
        )}

        {/* Logout */}
        <Button
          variant="ghost"
          size={collapsed ? 'icon' : 'default'}
          onClick={handleLogout}
          className={cn(
            'text-zinc-400 hover:text-red-400 hover:bg-zinc-800',
            !collapsed && 'w-full justify-start'
          )}
        >
          <LogOut className="w-5 h-5" />
          {!collapsed && <span className="ml-3">Déconnexion</span>}
        </Button>
      </div>
    </aside>
  );
};

export default Sidebar;
